import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Sparkles, ShieldCheck, GraduationCap, Building2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/site-pages.css';

/**
 * /about — company information (issue #263).
 *
 * Every claim on this page is drawn from something already published on the
 * site (the footer's company line and credits, and the Terms/Privacy copy in
 * PrivacyPolicy.js). Nothing here is invented: an Apple Developer reviewer
 * reads this page against the rest of the site, so a detail that exists only
 * here would be worse than no detail at all.
 *
 * `/learn-more` already exists and stays as-is — it is the product tour. This
 * page is the company, which is what Apple enrollment actually asks for.
 */
export default function AboutPage() {
  return (
    <div className="site-page">
      <Helmet>
        <title>About Us — TeachGenie™.ai</title>
        <meta
          name="description"
          content="TeachGenie™.ai is an AI teaching assistant built by YOM Private Limited, a DPIIT-recognised startup. Learn who we are, what we build, and how to reach us."
        />
        <link rel="canonical" href="https://www.teachgenie.ai/about" />
      </Helmet>

      <Navbar />

      <main className="site-page-inner">
        <span className="site-page-kicker">About Us</span>
        <h1>Built so teachers get their evenings back.</h1>
        <p className="site-page-lede">
          TeachGenie™.ai turns the parts of teaching that eat a weekend — lesson plans, decks,
          quizzes, diagrams — into minutes of work, and gives institutions a way to run
          assessments they can actually trust the results of.
        </p>

        <section className="site-section">
          <h2>What we build</h2>
          <div className="site-cards">
            <div className="site-card">
              <div className="site-card-icon"><Sparkles size={20} aria-hidden="true" /></div>
              <h3>TeachGenie™.ai</h3>
              <p>
                An AI teaching assistant that generates complete lesson plans, presentations,
                quizzes and visual diagrams from a topic — then lets a teacher edit every part
                of it. <Link to="/learn-more">See how it works</Link>.
              </p>
            </div>
            <div className="site-card">
              <div className="site-card-icon"><ShieldCheck size={20} aria-hidden="true" /></div>
              <h3>MARS</h3>
              <p>
                The Multi-modal Assessment Resilience Suite — behavioural proctoring that
                verifies evidence in real time, on whatever device a student sits the paper on.
              </p>
            </div>
            <div className="site-card">
              <div className="site-card-icon"><GraduationCap size={20} aria-hidden="true" /></div>
              <h3>The Fellowship</h3>
              <p>
                A programme for educators who want to shape what gets built next.{' '}
                <Link to="/become-a-fellow">Become a Fellow</Link>.
              </p>
            </div>
          </div>
        </section>

        <section className="site-section">
          <h2>How we think about AI</h2>
          <p>
            Everything the platform produces is a <strong>starting point for a teacher</strong>,
            not a replacement for one. Outputs are AI-generated and can be inaccurate,
            incomplete or out of date, so the product is built around review and editing rather
            than around trusting a first draft. We say the same thing in our{' '}
            <Link to="/terms">Terms of Service</Link> and in the disclaimer at the bottom of
            every page, because it is the honest description of what the tool is.
          </p>
        </section>

        <section className="site-section">
          <h2>The company</h2>
          <div className="site-cards">
            <div className="site-card">
              <div className="site-card-icon"><Building2 size={20} aria-hidden="true" /></div>
              <h3>YOM Private Limited</h3>
              <p>
                TeachGenie™.ai is operated by YOM Private Limited, a DPIIT-recognised startup
                incorporated in India, built and managed by Vertos of LPU.
              </p>
            </div>
          </div>
          <p style={{ marginTop: 18 }}>
            Ideated by <strong>Lovi Raj Gupta</strong>. Founding team:{' '}
            <strong>Dama Sri Ram</strong>, <strong>Prajithaa Parani</strong>,{' '}
            <strong>Golu Kumar</strong> and <strong>Medha Jha</strong>.
          </p>
        </section>

        <section className="site-section">
          <h2>Get in touch</h2>
          <p className="site-callout">
            General enquiries and privacy or grievance matters:{' '}
            <a href="mailto:info@teachgenie.ai">info@teachgenie.ai</a>. Full details are on the{' '}
            <Link to="/contact">contact page</Link>, and our{' '}
            <Link to="/policies">policies</Link> set out how we handle your data.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
